import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { CardPaymentSheet } from '@/nova/pages/wallet/components/CardPaymentSheet';
import { useCreditTopup } from '@/nova/hooks/useCreditTopup';
import { Button, Icon } from '../../../../components';
import { BAR_COLORS } from '../../../../theme/tokens';
import { CREDIT_CURRENCY, CREDIT_TOPUP_USD_PRESETS, formatCredits, usdToCredits } from '../../../../utils/credits';
import { currencySymbol, formatAmountWithCode } from '../../../../utils/money';
import {
  Body,
  Balance,
  BalanceMark,
  BalanceText,
  BalanceAmount,
  BalanceLabel,
  Explain,
  SectionLabel,
  PackRow,
  PackCard,
  PackTop,
  PackCredits,
  PackUnitRow,
  PackUnitIcon,
  PackUnit,
  PackPrice,
  Field,
  FieldInput,
  FieldUnit,
  FieldUsd,
  PayGroup,
  PayOption,
  RadioDot,
  PayText,
  PayLabel,
  PaySub,
  FundingNote,
  ErrorRow,
  ErrorText,
} from './CreditsModal.styles';

const LUMIO_SPARK_SRC = '/assets/acq/lumio-spark.svg';

export type CreditsModalProps = {
  onClose: () => void;
};

/** Cost in the credit's currency ("$25.00 USD"), a data value rendered as-is (not translated). */
const money = (amount: number): string => `${currencySymbol(CREDIT_CURRENCY)}${formatAmountWithCode(amount, CREDIT_CURRENCY)}`;
/** A wallet balance for the "available" note — symbol + amount, no currency code ("$47,857.64"). */
const avail = (amount: number): string =>
  `${currencySymbol(CREDIT_CURRENCY)}${Math.max(0, amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * AI-credits modal, opened from the header credits chip. Shows the balance (from the owner's AI_CREDITS
 * wallet) + what credits are for, and tops up: pick how many credits (money-anchored suggestions or a
 * custom amount), choose a payment method (Main wallet or card), and buy. The buy logic is shared with
 * the creatives Buy sheet ({@link useCreditTopup}).
 */
export const CreditsModal = ({ onClose }: CreditsModalProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  // Balance is the owner-level AI_CREDITS wallet (null until it exists — an honest skeleton, never a fake 0).
  const balance = useAppSelector((state) => state.ownerWallet?.credits ?? null);

  const topup = useCreditTopup(() => dismiss());
  const { credits, setCredits, rate, moneyCost, canPayFromMain, mainBalance, funding, settling, stalled } = topup;
  const [customText, setCustomText] = React.useState('');
  const [customFocused, setCustomFocused] = React.useState(false);
  const [method, setMethod] = React.useState<'wallet' | 'card'>('wallet');
  // Only a genuinely insufficient wallet forces card: `status: 'short'` means a REAL entered amount the
  // known balance can't cover. A zero/empty amount reports `canFund: false` but is NOT "not enough", so
  // gating on status (not canFund) keeps the wallet option quiet until there's an amount that overruns it.
  const walletShort = funding.status === 'short';
  const selected: 'wallet' | 'card' = walletShort ? 'card' : method;
  // Buying from the wallet needs the funds confirmed (canPayFromMain); card always works.
  const canBuy = credits > 0 && !settling && (selected === 'card' || canPayFromMain);

  // Money-anchored suggestions as their credit equivalent at the live rate.
  const presets = React.useMemo(
    () => CREDIT_TOPUP_USD_PRESETS.map((usd) => ({ usd, credits: usdToCredits(usd, rate) })),
    [rate],
  );

  const pickPreset = React.useCallback(
    (presetCredits: number) => {
      setCustomText('');
      setCredits(presetCredits);
    },
    [setCredits],
  );

  const onCustomChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const digits = event.currentTarget.value.replace(/[^0-9]/g, '');
      setCustomText(digits);
      setCredits(Math.max(0, Number(digits) || 0));
    },
    [setCredits],
  );

  const onBuy = React.useCallback(() => {
    if (selected === 'wallet') topup.payFromMain();
    else topup.openCard();
  }, [selected, topup]);

  const footer = stalled ? (
    <Button variant="primary" fullWidth label={t('Check again')} onClick={topup.retrySync} />
  ) : (
    <Button
      variant="primary"
      icon="plus"
      fullWidth
      disabled={!canBuy}
      label={
        topup.purchasing || settling
          ? t('Please wait…')
          : credits === 1
            ? t('Buy {{n}} credit', { n: formatCredits(credits) })
            : credits > 0
              ? t('Buy {{n}} credits', { n: formatCredits(credits) })
              : t('Buy credits')
      }
      onClick={onBuy}
    />
  );

  return (
    <>
      <Sheet
        title="AI credits"
        ariaLabel="AI credits"
        onClose={onClose}
        closeHandle={closeHandle}
        busy={topup.purchasing}
        animateResize
        footer={footer}
      >
        <Body>
          <Balance>
            <BalanceMark src={LUMIO_SPARK_SRC} alt="" aria-hidden="true" />
            <BalanceText>
              <BalanceAmount
                text={balance === 1 ? '{{count}} credit' : '{{count}} credits'}
                placeholders={{ count: balance != null ? formatCredits(balance) : '—' }}
              />
              <BalanceLabel text="Your balance" />
            </BalanceText>
          </Balance>

          <Explain text="Credits power Lumio's AI to generate visuals and content." />

          <div>
            <SectionLabel text="Top up" />
            <PackRow>
              {presets.map((preset) => (
                <PackCard
                  key={preset.usd}
                  type="button"
                  $selected={customText === '' && credits === preset.credits}
                  disabled={topup.purchasing}
                  onClick={() => pickPreset(preset.credits)}
                >
                  <PackTop>
                    <PackCredits text="{{n}}" placeholders={{ n: formatCredits(preset.credits) }} />
                    <PackUnitRow>
                      <PackUnitIcon src={LUMIO_SPARK_SRC} alt="" aria-hidden="true" />
                      <PackUnit text="AI credits" />
                    </PackUnitRow>
                  </PackTop>
                  <PackPrice text={money(preset.usd)} />
                </PackCard>
              ))}
            </PackRow>
          </div>

          <div>
            <SectionLabel text="Or a custom amount" />
            <Field $focused={customFocused}>
              <FieldInput
                value={customText}
                onChange={onCustomChange}
                onFocus={() => setCustomFocused(true)}
                onBlur={() => setCustomFocused(false)}
                inputMode="numeric"
                placeholder={t('e.g. 750')}
                aria-label={t('Custom credit amount')}
                disabled={topup.purchasing}
              />
              <FieldUnit text="credits" />
              {customText !== '' && credits > 0 ? <FieldUsd text={money(moneyCost)} /> : null}
            </Field>
          </div>

          <div>
            <SectionLabel text="Payment" />
            <PayGroup role="radiogroup" aria-label={t('Payment method')}>
              <PayOption
                type="button"
                role="radio"
                aria-checked={selected === 'wallet'}
                $selected={selected === 'wallet'}
                $disabled={walletShort}
                disabled={topup.purchasing}
                onClick={() => {
                  if (!walletShort) setMethod('wallet');
                }}
              >
                <RadioDot $on={selected === 'wallet'} aria-hidden="true" />
                <PayText>
                  <PayLabel text="Pay from Main wallet" />
                  {mainBalance == null ? (
                    <PaySub text="Checking your balance…" />
                  ) : walletShort ? (
                    <PaySub text="Not enough · {{amount}} available" placeholders={{ amount: avail(mainBalance) }} />
                  ) : (
                    <PaySub text="{{amount}} available" placeholders={{ amount: avail(mainBalance) }} />
                  )}
                </PayText>
              </PayOption>

              <PayOption
                type="button"
                role="radio"
                aria-checked={selected === 'card'}
                $selected={selected === 'card'}
                disabled={topup.purchasing}
                onClick={() => setMethod('card')}
              >
                <RadioDot $on={selected === 'card'} aria-hidden="true" />
                <PayText>
                  <PayLabel text="Pay by card" />
                </PayText>
              </PayOption>
            </PayGroup>
          </div>

          {settling ? (
            <FundingNote text="Updating your balance…" />
          ) : topup.purchaseError ? (
            <ErrorRow role="alert">
              <Icon name="warning" size={14} color={BAR_COLORS.danger} />
              <ErrorText>{topup.purchaseError}</ErrorText>
            </ErrorRow>
          ) : null}
        </Body>
      </Sheet>

      {topup.cardOpen ? (
        <CardPaymentSheet
          amountLabel={money(moneyCost)}
          createIntent={topup.mintCardIntent}
          onSuccess={topup.onCardPaid}
          onClose={topup.closeCard}
        />
      ) : null}
    </>
  );
};

export default CreditsModal;
